import { createFileRoute, redirect } from "@tanstack/react-router";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminPageLayout } from "@/features/admin/components/AdminPageLayout";
import { SessionNotificationsSection } from "@/features/admin/instructor-led/SessionNotificationsSection";
import { SessionRosterPage } from "@/features/admin/instructor-led/SessionRosterPage";
import { SessionTrainersSection } from "@/features/admin/instructor-led/SessionTrainersSection";
import { SessionResourcesSection } from "@/features/instructor-led/resources/SessionResourcesSection";
import { fetchFeatureFlags } from "@/hooks/useFeatureFlags";
import { toNum } from "@/utils/course-formatters";

export const Route = createFileRoute("/admin/courses/$courseId_/sessions/$sessionId")({
  // The roster is hidden while the ilt flag is off (the API 404s it too): back to the sessions.
  beforeLoad: async ({ params }) => {
    const { ilt } = await fetchFeatureFlags();
    if (!ilt)
      throw redirect({
        to: "/admin/courses/$courseId",
        params: { courseId: params.courseId },
        search: { tab: "sessions" },
      });
  },
  component: SessionRosterRoute,
  remountDeps: ({ params }) => `${params.courseId}/${params.sessionId}`,
});

/**
 * A session's admin page: its trainers (LMS-137), the files and links shared with its learners
 * (LMS-143), who has booked it, marking their attendance (LMS-38), and its planned emails, with
 * failed sends flagged (LMS-207).
 */
function SessionRosterRoute() {
  const { courseId, sessionId } = Route.useParams();

  return (
    <ProtectedRoute requiredPermissions={["Courses.Read"]} requiredRoles={["Admin"]}>
      <AdminPageLayout mainClassName="bg-secondary_alt">
        <SessionRosterPage
          courseId={courseId}
          sessionId={sessionId}
          renderTrainers={(roster) => (
            <SessionTrainersSection
              courseId={courseId}
              sessionId={sessionId}
              isCancelled={Boolean(roster.isCancelled)}
              learnersBooked={toNum(roster.seatsBooked)}
              hasEnded={Boolean(roster.hasEnded)}
            />
          )}
          renderResources={() => <SessionResourcesSection sessionId={sessionId} />}
          renderNotifications={() => (
            <SessionNotificationsSection courseId={courseId} sessionId={sessionId} />
          )}
        />
      </AdminPageLayout>
    </ProtectedRoute>
  );
}
